/* global Modernizr TweenMax */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import isString from 'lodash/isString';

import Extras from '../../data/extras.json';
import Texts from '../../data/texts.json';
import Audio from '../../lib/audio';
import VideoPlayer from './VideoPlayer.jsx';

const propTypes = {
    opened: PropTypes.bool,
    visible: PropTypes.bool,

    title: PropTypes.string,
    description: PropTypes.string,

    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    visibilityAnimationDuration: PropTypes.number,

    onChange: PropTypes.func,
    onClosed: PropTypes.func,
    onButtonClick: PropTypes.func,
    contentMaxWidth: PropTypes.string,
};

const defaultProps = {
    opened: false,
    visible: false,
    label: Texts.general.learnMore,

    title: 'Default title',
    description: 'Default description',

    visibilityAnimationDuration: 0.3,
    openAnimationDuration: 1,

    onChange: null,
    onClosed: null,
    onButtonClick: null,
};

const contextTypes = {
    language: PropTypes.string,
    learnMoreContent: PropTypes.object,
    learnMoreKey: PropTypes.string,
};

class LearnMore extends Component {

    static onPreventStop(e) {
        if (e) {
            e.stopPropagation();
        }
    }

    constructor(props) {
        super(props);

        this.audios = null;
        this.learnMoreElement = null;

        this.onToggleUp = this.onToggleUp.bind(this);
        this.onToggleDown = this.onToggleDown.bind(this);

        this.onCloseClick = this.onCloseClick.bind(this);
        this.onToggleClick = this.onToggleClick.bind(this);
    }

    /*
     * Life cycle
     */

    componentDidMount() {
        this.audios = {
            appear: new Audio('/audio/open-help-menu.mp3'),
            open: new Audio('/audio/hint-button-v2-gd.mp3'),
            close: new Audio('/audio/menu-back.mp3'),
        };
        this.updateVisibility(this.props.visible, 0);
    }

    componentDidUpdate(prevProps) {
        const visibleChanged = prevProps.visible !== this.props.visible;
        const openedChanged = prevProps.opened !== this.props.opened;

        if (openedChanged) {
            this.updatePosition();
            if (!this.props.visible) {
                this.updateVisibility(this.props.opened);
            }
        }

        if (visibleChanged) {
            this.updateVisibility(this.props.visible);
        }
    }

    componentWillUnmount() {
        this.audios = null;
    }

    /*
     * Events handlers
     */

    onToggleClick(e) {
        if (this.props.onButtonClick !== null) {
            this.props.onButtonClick(!this.props.opened);
        }
    }

    onCloseClick(e) {
        if (this.props.onButtonClick !== null) {
            this.props.onButtonClick(false);
        }
    }

    onToggleDown(e) {
        e.stopPropagation();
    }

    onToggleUp(e) {
        e.stopPropagation();
    }

    /*
     * Methods
     */

    updateVisibility(visible, duration) {
        const time = typeof duration !== 'undefined' && duration !== null ?
            duration : this.props.visibilityAnimationDuration;
        const el = this.learnMoreElement;

        if (el !== null) {
            TweenMax.to(el, time, {
                autoAlpha: visible ? 1 : 0,
            });
        }
    }

    updatePosition() {
        const opened = this.props.opened;
        const height = this.getTransitionDeltaHeight();
        const duration = this.props.openAnimationDuration;

        if (opened) {
            this.audios.open.play();

            if (this.props.onOpened) {
                this.props.onOpened(height, duration);
            }
        } else {
            this.audios.close.play();

            if (this.props.onClosed) {
                this.props.onClosed(height, duration);
            }
        }
    }

    getTransitionDeltaHeight() {
        if (this.learnMoreElement !== null) {
            const el = this.learnMoreElement;
            return el.offsetHeight + 2;
        }
    }

    /*
     * Render
     */

    renderCloseButton() {
        const userEvents = {
            onClick: this.onCloseClick,
        };

        if (Modernizr.touchevents) {
            userEvents.onTouchStart = this.onToggleDown;
            userEvents.onTouchEnd = this.onToggleUp;
        } else {
            userEvents.onMouseDown = this.onToggleDown;
            userEvents.onMouseUp = this.onToggleUp;
        }

        return (
            <div className="close-button-container">
                <button className="close-button" type="button" {...userEvents} />
            </div>
        );
    }

    renderOpenedContent() {
        const { opened } = this.props;
        const { learnMoreKey } = this.context;

        if (!learnMoreKey) return null;

        const containerStyle = {
            maxWidth: learnMoreKey === 'conclusion' ? null : this.props.contentMaxWidth,
        };

        return (
            <div className="opened-content">
                <div className="content">
                    <div className="content-inner">
                        <div className="container-content" style={containerStyle}>
                            <VideoPlayer
                                name={learnMoreKey}
                                paused={opened}
                                canMute={false}
                                onEnd={this.onCloseClick}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { width, height } = this.props;
        const className = `learn-more-container ${this.props.opened ? ' opened' : ''}`;
        const closeButton = this.renderCloseButton();
        const openedContent = this.renderOpenedContent();

        const style = {
            width,
            height,
        };

        return (
            <div
                key={this.context.learnMoreKey}
                className={className}
                ref={(el) => { this.learnMoreElement = el; }}
                style={style}
            >
                { closeButton }
                { openedContent }
            </div>
        );
    }
}

LearnMore.propTypes = propTypes;
LearnMore.defaultProps = defaultProps;
LearnMore.contextTypes = contextTypes;

module.exports = LearnMore;



// WEBPACK FOOTER //
// ./js/components/partials/LearnMore.jsx